import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import PriceChangedModal from "./PriceChangeModal";
import AuthExpiredModal from "./AuthExpiredModal";
import AddressPopup from "./AddressPopup";
import MiniRegisterModal from "./MiniRegisterModal";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

// Price
const Price = styled.p`
  font-size: 0.9rem;
  color: ${({ $muted }) => ($muted ? "#9ca3af" : "#000")};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  margin: 1rem 0;
  font-weight: 700;
`;

const Muted = styled.span`
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  filter: ${({ $muted }) => ($muted ? "grayscale(40%)" : "none")};
`;

const StockNote = styled.div`
  margin-top: 6px;
  font-size: 0.85rem;
  font-weight: 800;
  color: #ef4444;
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,.35);
  z-index: 99999;
  display: grid; place-items: center;
`;

const ModalCard = styled.div`
  width: min(520px, 92vw);
  background: #fff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,.08);
  padding: 18px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 4px 0 8px;
  font-weight: 900;
  color: #111827;
`;

const ModalP = styled.p`
  margin: 0 0 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex; justify-content: center; gap: 10px; margin-top: 6px;
`;

const ModalBtn = styled.button`
  height: 40px; padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,.08);
  background: #111827; color: #fff;
  font-weight: 800;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); filter: brightness(.95); }
`;

const MlThumb = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
`;

const PayOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 999999;
  display: grid;
  place-items: center;
  padding: 12px;
`;

const PayShell = styled.div`
  width: min(980px, 96vw);
  height: min(92vh, 860px);
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(0,0,0,.08);
  box-shadow: 0 18px 50px rgba(0,0,0,.18);
  overflow: hidden;

  display: flex;
  flex-direction: column;

  /* iOS safe-area */
  padding-bottom: env(safe-area-inset-bottom);
`;

const PayHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #fff;
`;

const PayTitle = styled.div`
  font-weight: 900;
  color: #111827;
  letter-spacing: .01em;
`;

const PayClose = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,.08);
  background: #111827;
  color: #fff;
  font-size: 22px;
  font-weight: 900;
  cursor: pointer;
  transition: transform .12s ease, filter .12s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.95); }
`;

const PayHint = styled.div`
  flex: 0 0 auto;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 1px solid rgba(0,0,0,.06);
  background: #fafafa;
`;

const PayFrameWrap = styled.div`
  flex: 1 1 auto;
  min-height: 0; /* 🔥 ეს არის მთავარი რომ iframe არ მოიჭრას flex-ში */
  background: #fff;
`;

const PayFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;


function StockModal({ message, onClose }) {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalTitle>⚠️</ModalTitle>
        <ModalP>{message}</ModalP>
        <ModalActions>
          <ModalBtn onClick={onClose}>OK</ModalBtn>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
}

const API_BASE_URL = "https://shopaholics.ge/api";

/* ====================== helpers for mixed strategies ====================== */

const isInsufficientFunds = (data = {}) => {
  const err  = String(data?.error || data?.reason || "").toLowerCase();
  const code = String(data?.error_code || data?.response_code || "").toLowerCase();
  const desc = String(data?.response_description || data?.error_message || data?.message || "").toLowerCase();

  const hay = `${err} ${code} ${desc}`;

  // ---- heuristics (works even if gateway uses different codes) ----
  return (
    err === "recurring_failed" && (
      hay.includes("insufficient") ||
      hay.includes("not enough") ||
      hay.includes("funds") ||
      hay.includes("balance") ||
      hay.includes("no money") ||
      hay.includes("51") ||        // common "insufficient funds" code in some gateways
      hay.includes("insuf") ||
      hay.includes("saldo")
    )
  ) || (
    // sometimes backend may directly send insufficient_funds
    hay.includes("insufficient_funds") ||
    hay.includes("not_enough_funds")
  );
};

const paymentFailText = (data = {}, language = "ka") => {
  if (isInsufficientFunds(data)) {
    return language === "en"
      ? "Your card balance is insufficient. Please use another card or top up and try again."
      : "ბარათზე თანხა არ არის საკმარისი. სცადეთ სხვა ბარათი ან შეავსეთ ბალანსი და ხელახლა სცადეთ.";
  }

  const msg =
    data?.response_description ||
    data?.error_message ||
    data?.error ||
    data?.reason ||
    "";

  return language === "en"
    ? (msg ? `Payment failed: ${msg}` : "Payment failed. Please try again.")
    : (msg ? `გადახდა ვერ შესრულდა: ${msg}` : "გადახდა ვერ შესრულდა. სცადეთ ხელახლა.");
};

const isUnitsRow = (ml, item) => {
  // explicit strategy on row wins
  if (ml?.stock_strategy === "units") return true;
  if (ml?.stock_strategy === "full") return false;
  // backward-compat: per_ml_stock (all per-ML before)
  return !!item?.per_ml_stock && ml?.unit_stock != null;
};
const isFullRow = (ml, item) => !isUnitsRow(ml, item);

/** total selected ML amount for pooled "full" rows only (in milliliters) */
const selectedMlTotalFullRows = (selectedList = []) =>
  selectedList.reduce((sum, x) => {
    if (!x.isSelected) return sum;
    if (x._strategy === "full") {
      return sum + Number(x.quantity || 0) * Number(x.size || 0);
    }
    return sum;
  }, 0);

/** decorate an ML row with a computed _strategy flag */
const withStrategy = (ml, item) => ({
  ...ml,
  _strategy: isUnitsRow(ml, item) ? "units" : "full",
});


function PaymentIframeModal({ url, onClose, language }) {
  const T = (ka, en) => (language === "ka" ? ka : en);

  return (
    <PayOverlay onClick={onClose}>
      <PayShell onClick={(e) => e.stopPropagation()}>
        {/* Header (fixed) */}
        <PayHeader>
          <PayTitle>{T("გადახდის დადასტურება", "Confirm payment")}</PayTitle>
          <PayClose onClick={onClose} aria-label="Close">×</PayClose>
        </PayHeader>

        {/* Optional hint row (small) */}
        <PayHint>
          {T(
            "შეიყვანეთ SMS კოდი და დაასრულეთ გადახდა. თუ არაფერი ჩანს, დააჭირეთ Close და გავხსნით ჩვეულებრივად.",
            "Enter the SMS code to finish. If it’s blank, close and we’ll open it normally."
          )}
        </PayHint>

        {/* Iframe fills remaining height */}
        <PayFrameWrap>
          <PayFrame
            title="payment"
            src={url}
            allow="payment *; clipboard-read *; clipboard-write *"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </PayFrameWrap>
      </PayShell>
    </PayOverlay>
  );
}


/* ======================================================================== */

const CartOverlay = ({ isOpen, cart, setCart, toggleCartOverlay, deleteItemFromCart }) => {
  const { t, language } = useLanguage();
  const [userId, setUserId] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const cartOverlayRef = useRef(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [pendingAddressData, setPendingAddressData] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const { version } = useDataRefresh();
  const pricesBusyRef = useRef(false);

  const [popupAssumeHasPhone, setPopupAssumeHasPhone] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockMessage, setStockMessage] = useState("");
  const T = (ka, en) => (language === "ka" ? ka : en);
  const [payModalOpen, setPayModalOpen] = useState(false);
const [payUrl, setPayUrl] = useState("");
const [deliveryFee, setDeliveryFee] = useState(0);

useEffect(() => {
  const fetchSettings = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/get_settings.php`);
      const fee =
        res.data?.delivery_fee ??
        res.data?.settings?.delivery_fee ??
        0;

      setDeliveryFee(Number(fee || 0));
    } catch (e) {
      console.error("delivery fee fetch error", e);
      setDeliveryFee(0);
    }
  };

  fetchSettings();
}, []);

  const syncCartToServer = async (nextCart) => {
    try {
      const storedUser = localStorage.getItem("user");
      const u = storedUser ? JSON.parse(storedUser) : null;
      if (!u?.id) return;
      await api.post(`${API_BASE_URL}/merge_cart.php?t=${Date.now()}`, {
        user_id: u.id,
        cart: nextCart,
      });
    } catch (e) {
      console.error("merge_cart sync failed", e);
    }
  };

  const firstTickRef = useRef(true);
  useEffect(() => {
    if (firstTickRef.current) { firstTickRef.current = false; return; }
    if (pricesBusyRef.current) return;
    pricesBusyRef.current = true;
    (async () => {
      await refetchCartPrices();
      pricesBusyRef.current = false;
    })();
  }, [version]);

  useEffect(() => {
    if (!isOpen) return;
    if (pricesBusyRef.current) return;
    pricesBusyRef.current = true;
    (async () => {
      await refetchCartPrices();
      pricesBusyRef.current = false;
    })();
  }, [isOpen]);

  const giftSetParams = (rawId) => {
    const id = String(rawId ?? "");
    return /^\d+$/.test(id) ? { id } : { gift_set_id: id };
  };

  // 🔄 refresh prices from backend (perfume/accessory/gift_set) — now preserves ML strategy
  const refetchCartPrices = async () => {
    if (!Array.isArray(cart) || cart.length === 0) return;

    try {
      // 1) products base
      const { data: products } = await api.get(`${API_BASE_URL}/get_products.php`);
      const list = Array.isArray(products) ? products : [];
      const pMap = new Map(list.map(p => [String(p.product_id), p]));

      // 2) gift sets to fetch
      const giftSetItems = cart
        .map((it, idx) => ({ it, idx }))
        .filter(x => x.it.type === 'gift_set')
        .map(x => ({
          idx: x.idx,
          id: String(x.it.gift_set_id || x.it.product_id || x.it.id),
        }));

      // 3) fetch gift set details
      const gsUpdates = await Promise.all(
        giftSetItems.map(async g => {
          try {
            const { data } = await api.get(
              `https://shopaholics.ge/api/gift_sets/gift_set_public_detail.php`,
              { params: giftSetParams(g.id) }
            );
            return { idx: g.idx, data: data?.success ? data : null };
          } catch {
            return { idx: g.idx, data: null };
          }
        })
      );

      // 4) build next — ML rows keep/refresh prices, images, unit_stock, stock_strategy
      const buildNext = (base) =>
        base.map((item, i) => {
          if (item.type === 'perfume' || item.category === 'perfume' || item.type === 'accessory') {
            const fresh = pMap.get(String(item.product_id));
            if (!fresh) return item;

            if (item.type === 'perfume' || item.category === 'perfume') {
              const freshMl = Array.isArray(fresh.milliliters) ? fresh.milliliters : [];
              const mlMap = new Map(
                freshMl.map(m => [String(m.size), m])
              );

              const updatedMl = (Array.isArray(item.milliliters) ? item.milliliters : []).map(ml => {
                const f = mlMap.get(String(ml.size));
                const base = f || ml;
                const merged = {
                  ...ml,
                  price: base.price,
                  sale_price: base.sale_price,
                  images: Array.isArray(base.images) ? base.images
                        : (Array.isArray(ml.images) ? ml.images : []),
                  stock_strategy: base.stock_strategy ?? ml.stock_strategy, // <-- keep strategy if backend provides
                };
                if (isUnitsRow(merged, item)) {
                  merged.unit_stock = Number(
                    base.unit_stock != null ? base.unit_stock
                    : (ml.unit_stock != null ? ml.unit_stock : 0)
                  );
                }
                return merged;
              });

              // if there is ANY "full" row, trust product full_stock; otherwise, keep prior
              const hasFullRows = updatedMl.some(x => !isUnitsRow(x, item));
              return {
                ...item,
                ...(hasFullRows ? { full_stock: fresh.full_stock ?? item.full_stock } : { full_stock: item.full_stock }),
                milliliters: updatedMl,
                image: (fresh.imageUrls && fresh.imageUrls[0]) || item.image
              };
            }

            // accessory (variant / non-variant) keeps your logic
            if (item.accessory_variant_id) {
              const variants =
                (Array.isArray(fresh?.variants) && fresh.variants) ||
                (Array.isArray(fresh?.accessory_variants) && fresh.accessory_variants) ||
                null;

              const v = variants
                ? variants.find(x => String(x.id) === String(item.accessory_variant_id))
                : null;

              if (v) {
                const vPrice = Number(v?.price ?? item.price_p ?? 0);
                const vSale  = (v?.sale_p != null) ? Number(v.sale_p) : null;
                const useSale = vSale != null && vSale > 0 && vSale < vPrice;

                return {
                  ...item,
                  full_stock: Number(v?.stock ?? item.full_stock ?? 0),
                  price_p: vPrice,
                  sale_p: useSale ? vSale : null,
                  image:
                    (Array.isArray(v?.images) && v.images[0]) ||
                    item.image ||
                    (fresh.imageUrls && fresh.imageUrls[0]) ||
                    ""
                };
              }

              return {
                ...item,
                full_stock: item.full_stock,
                price_p: item.price_p,
                sale_p: item.sale_p,
                image: item.image || (fresh.imageUrls && fresh.imageUrls[0]) || ""
              };
            }

            return {
              ...item,
              full_stock: Number(fresh.full_stock ?? item.full_stock ?? 0),
              price_p: Number(fresh.price_p ?? item.price_p ?? 0),
              sale_p: fresh.sale_p ?? item.sale_p,
              image: (fresh.imageUrls && fresh.imageUrls[0]) || item.image || ""
            };
          }

          if (item.type === 'gift_set') {
            const found = gsUpdates.find(g => g.idx === i);
            if (found?.data) {
              const d = found.data;
              return {
                ...item,
                unit_price: Number(d.final_price || item.unit_price || 0),
                full_stock: Number(d.full_stock ?? item.full_stock ?? 0),
                image: (Array.isArray(d.images) && d.images[0]) || item.image
              };
            }
          }

          return item;
        });

      let computedNext = null;
      setCart(prev => {
        const next = buildNext(prev);
        computedNext = next;
        try {
          if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
        } catch {}
        try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
        return next;
      });

      // ✅ rebuild selectedOptions to mirror strategies on rows
      if (computedNext) {
        const nextSel = computedNext.map(item => {
          if (item.type === "perfume" && Array.isArray(item.milliliters)) {
            const bySize = new Map(
              (item.selected_ml || []).map(r => [String(r.size), Number(r.quantity || 0)])
            );
            return {
              selectedMilliliters: item.milliliters.map(raw => {
                const ml = withStrategy(raw, item);
                const q = bySize.get(String(ml.size)) || 0;
                return { ...ml, isSelected: q > 0, quantity: q };
              }),
            };
          }
          return { selectedMilliliters: [] };
        });
        setSelectedOptions(nextSel);
        try { localStorage.setItem("selectedOptions", JSON.stringify(nextSel)); } catch {}
      }
    } catch (e) {
      console.warn("refetchCartPrices failed", e);
    }
  };

  async function openAddressPopup() {
    const me = await fetchMe();
    setPopupAssumeHasPhone(!!me?.has_phone);
    setShowAddressPopup(true);
  }

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUserId(userData.id);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddressPopup || showRegisterModal || showPriceModal || showAuthModal) return;
      if (cartOverlayRef.current && !cartOverlayRef.current.contains(event.target) && isOpen) {
        toggleCartOverlay();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => { document.removeEventListener("mousedown", handleClickOutside); };
  }, [isOpen, toggleCartOverlay, showAddressPopup, showRegisterModal, showPriceModal, showAuthModal]);

  // initial selectedOptions build (respects row strategies)
  useEffect(() => {
    const next = cart.map(item => {
      if (item.type === "perfume" && Array.isArray(item.milliliters)) {
        const bySize = new Map(
          (item.selected_ml || []).map(r => [String(r.size), Number(r.quantity || 0)])
        );
        return {
          selectedMilliliters: item.milliliters.map(raw => {
            const ml = withStrategy(raw, item);
            const q = bySize.get(String(ml.size)) || 0;
            return { ...ml, isSelected: q > 0, quantity: q };
          }),
        };
      }
      return { selectedMilliliters: [] };
    });

    setSelectedOptions(next);
    try { localStorage.setItem("selectedOptions", JSON.stringify(next)); } catch {}
  }, [cart]);

  /* ======================= mixed-strategy selection ======================= */

  const handleMilliliterChange = (perfumeIndex, size, isSelected) => {
    setSelectedOptions(prevOptions => {
      const updatedOptions = prevOptions.map((option, index) => {
        if (index !== perfumeIndex) return option;

        const item = cart[perfumeIndex];
        const fullCap = Number(item?.full_stock || 0);

        // compute current selected full-rows total (ml)
        const currentSelected = option.selectedMilliliters.map(ml => withStrategy(ml, item));
        const currentFullTotal = selectedMlTotalFullRows(currentSelected);

        const nextSel = currentSelected.map(ml => {
          if (ml.size !== size) return ml;

          if (!isSelected) return { ...ml, isSelected: false, quantity: 0 };

          if (ml._strategy === "units") {
            const max = Number(ml.unit_stock || 0);
            const desired = Math.max(1, ml.quantity || 1);
            return { ...ml, isSelected: true, quantity: Math.min(desired, max) };
          } else {
            // pooled(“full”) — compute remaining pool excluding this row’s current usage
            const thisUsage = Number(ml.quantity || 0) * Number(ml.size || 0);
            const poolRemain = Math.max(0, fullCap - (currentFullTotal - thisUsage));
            const maxQty = Math.floor(poolRemain / Number(ml.size || 1));
            if (maxQty <= 0) return { ...ml, isSelected: false, quantity: 0 };
            const desired = Math.max(1, ml.quantity || 1);
            return { ...ml, isSelected: true, quantity: Math.min(desired, maxQty) };
          }
        });

        return { ...option, selectedMilliliters: nextSel };
      });

      try { localStorage.setItem("selectedOptions", JSON.stringify(updatedOptions)); } catch {}
      return updatedOptions;
    });

    // sync into cart.selected_ml
    setCart(prevCart => {
      const next = prevCart.map((it, i) => {
        if (i !== perfumeIndex || it.type !== "perfume") return it;

        const mls = Array.isArray(it.milliliters) ? it.milliliters.map(r => withStrategy(r, it)) : [];
        const bySize = new Map((it.selected_ml || []).map(r => [String(r.size), Number(r.quantity||0)]));

        const row = mls.find(m => Number(m.size) === Number(size));
        if (!row) return it;

        if (!isSelected) {
          bySize.set(String(size), 0);
        } else if (row._strategy === "units") {
          const max = Number(row.unit_stock || 0);
          const curr = bySize.get(String(size)) || 0;
          bySize.set(String(size), Math.min(Math.max(1, curr || 1), max));
        } else {
          // pooled
          const fullCap = Number(it.full_stock || 0);
          const totalWithoutThis = Array.from(bySize.entries()).reduce((s,[k,q]) => {
            if (k === String(size)) return s;
            const other = mls.find(m=>String(m.size)===k);
            if (!other || other._strategy !== "full") return s;
            return s + q * Number(other.size || 0);
          }, 0);
          const remain = Math.max(0, fullCap - totalWithoutThis);
          const sz = Number(row.size || 1);
          const maxQty = Math.floor(remain / sz);
          const curr = bySize.get(String(size)) || 0;
          const desired = Math.max(1, curr || 1);
          bySize.set(String(size), Math.min(desired, maxQty));
        }

        const selected_ml = mls
          .map(ml => {
            const q = bySize.get(String(ml.size)) || 0;
            return q > 0 ? { size: Number(ml.size), quantity: Number(q) } : null;
          })
          .filter(Boolean);

        const quantity = selected_ml.reduce((s, r) => s + Number(r.quantity || 0), 0) || 1;
        return { ...it, selected_ml, quantity };
      });

      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      syncCartToServer(next);
      return next;
    });
  };

  const handleQuantityChange = (perfumeIndex, mlSize, value) => {
    setSelectedOptions(prevOptions =>
      prevOptions.map((option, index) => {
        if (index !== perfumeIndex) return option;

        const item = cart[perfumeIndex];
        const fullCap = Number(item?.full_stock || 0);
        const mls = option.selectedMilliliters.map(ml => withStrategy(ml, item));

        const idx = mls.findIndex(ml => Number(ml.size) === Number(mlSize));
        if (idx === -1) return option;

        const current = mls[idx];
        const desired = Math.max(1, parseInt(value, 10) || 1);

        if (current._strategy === "units") {
          const max = Number(current.unit_stock || 0);
          mls[idx] = { ...current, isSelected: desired > 0, quantity: Math.min(desired, max) };
        } else {
          // pooled: cap only against other "full" rows
          const totalWithoutThis = mls.reduce((s, x, i) =>
            s + (i === idx || !x.isSelected || x._strategy !== "full"
                  ? 0
                  : (Number(x.quantity||0) * Number(x.size||0))),
            0
          );
          const remainMl = Math.max(0, fullCap - totalWithoutThis);
          const maxForThis = Math.floor(remainMl / Number(current.size || 1));
          const nextQty = Math.min(desired, maxForThis);
          mls[idx] = { ...current, isSelected: nextQty > 0, quantity: nextQty };
        }

        return { ...option, selectedMilliliters: mls };
      })
    );

    // cart sync (same logic as above)
    setCart(prevCart => {
      const next = prevCart.map((it, i) => {
        if (i !== perfumeIndex || it.type !== "perfume") return it;

        const mls = Array.isArray(it.milliliters) ? it.milliliters.map(r => withStrategy(r, it)) : [];
        const row = mls.find(m => Number(m.size) === Number(mlSize));
        if (!row) return it;

        const bySize = new Map((it.selected_ml || []).map(r => [String(r.size), Number(r.quantity||0)]));
        const desired = Math.max(1, parseInt(value, 10)||1);

        if (row._strategy === "units") {
          bySize.set(String(mlSize), Math.min(desired, Number(row.unit_stock||0)));
        } else {
          const fullCap = Number(it.full_stock || 0);
          const totalWithoutThis = Array.from(bySize.entries()).reduce((s,[k,q]) => {
            if (k === String(mlSize)) return s;
            const other = mls.find(m=>String(m.size)===k);
            if (!other || other._strategy !== "full") return s;
            return s + q * Number(other.size || 0);
          }, 0);
          const remainMl = Math.max(0, fullCap - totalWithoutThis);
          const maxForThis = Math.floor(remainMl / Number(row.size || 1));
          bySize.set(String(mlSize), Math.min(desired, maxForThis));
        }

        const selected_ml = mls
          .map(ml => {
            const q = bySize.get(String(ml.size)) || 0;
            return q > 0 ? { size: Number(ml.size), quantity: Number(q) } : null;
          })
          .filter(Boolean);

        const quantity = selected_ml.reduce((s, r) => s + Number(r.quantity || 0), 0) || 1;
        return { ...it, selected_ml, quantity };
      });

      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      syncCartToServer(next);
      return next;
    });
  };

  /* ======================= totals / pricing ======================= */

  const totalItems = cart.reduce((sum, item, index) => {
    if (item.type === "perfume") {
      return sum + (selectedOptions[index]?.selectedMilliliters.reduce(
        (mlSum, ml) => (ml.isSelected ? (mlSum + (Number(ml.quantity||0))) : mlSum), 0
      ) || 0);
    } else if (item.type === "accessory") {
      return sum + (item.quantity || 0);
    } else if (item.type === "gift_set") {
      return sum + (item.quantity || 0);
    }
    return sum;
  }, 0);

  const totalPrice = cart.reduce((sum, item, index) => {
    if (item.type === "perfume") {
      const perfumeTotal = selectedOptions[index]?.selectedMilliliters.reduce((mlSum, ml) =>
        ml.isSelected
          ? mlSum + (
              (ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price)
                ? parseFloat(ml.sale_price)
                : parseFloat(ml.price)) * Number(ml.quantity || 0)
            )
          : mlSum,
        0
      );
      return sum + perfumeTotal;
    } else if (item.type === "accessory") {
      const priceToUse = item.sale_p && parseFloat(item.sale_p) < parseFloat(item.price_p)
        ? parseFloat(item.sale_p)
        : parseFloat(item.price_p);
      return sum + (priceToUse * (item.quantity || 0));
    } else if (item.type === "gift_set") {
      const unit = Number(item.unit_price || 0);
      return sum + unit * (item.quantity || 0);
    }
    return sum;
  }, 0).toFixed(2);

  async function fetchMe() {
    try {
      const res = await api.get(`${API_BASE_URL}/me.php`, { withCredentials: true });
      return res.data;
    } catch {
      return { auth: false };
    }
  }

  api.defaults.withCredentials = true;

  const handleBuyNow = async (addressData) => {
    const me = await fetchMe();
    const buyerId =
      (me?.auth && (me.user?.id || me.guest_user_id || me.user_id || me.user?.user_id)) ||
      tempUser?.id;

    if (!buyerId) {
      setShowRegisterModal(true);
      return false;
    }

    // build purchase items (unchanged)
    const purchaseItems = cart.flatMap((item, index) => {
      if (item.type === "perfume") {
        return (selectedOptions[index]?.selectedMilliliters || [])
          .filter((ml) => ml.isSelected && ml.quantity > 0)
          .map((ml) => ({
            product_id: item.product_id,
            product_type: "perfume",
            size: ml.size,
            quantity: ml.quantity,
            price:
              ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price)
                ? ml.sale_price
                : ml.price,
          }));
      }

      if (item.type === "accessory") {
        if (item.quantity > 0) {
          const payload = {
            product_id: item.product_id,
            product_type: "accessory",
            quantity: item.quantity,
            price:
              item.sale_p && parseFloat(item.sale_p) < parseFloat(item.price_p)
                ? item.sale_p
                : item.price_p,
          };
          if (item.accessory_variant_id) {
            payload.accessory_variant_id = item.accessory_variant_id;
          }
          return payload;
        }
      }

      if (item.type === "gift_set") {
        if ((item.quantity || 0) > 0) {
          return {
            product_id:  item.gift_set_id || item.product_id || String(item.id),
            product_type: "gift_set",
            quantity:    item.quantity,
            price:       Number(item.unit_price || 0),
          };
        }
      }

      return [];
    }).filter(Boolean);

    if (purchaseItems.length === 0) {
      setStockMessage(
        T(
          "არჩევანი ცარიელია. სუნამოებისთვის აირჩიეთ ზომა/რაოდენობა, სხვა ნივთებისთვის მიუთითეთ რაოდენობა.",
          "Your selection is empty. Select size/quantity for perfumes or set a quantity for other items."
        )
      );
      setStockModalOpen(true);
      return;
    }

    const payment_method_token = String(addressData?.payment_method_token || "");
    const save_card = Number(addressData?.save_card || 0);


    const purchaseData = {
      user_id: buyerId,
      items: purchaseItems,
      address: addressData?.address || "",
      city_village: addressData?.city_village || addressData?.city || "",
      comment: addressData?.comment || "",
      payment_method_token,
      save_card,
      purchase_date: new Date().toISOString(),
    };

    try {
      // stock check with expected_unit_price
      const validationItems = purchaseItems.map(it => {
        let expected = Number(it.price || 0);
        const base = {
          product_id: String(it.product_id),
          product_type: it.product_type,
          quantity: Number(it.quantity || 0),
          size: it.size != null ? Number(it.size) : undefined,
          expected_unit_price: expected
        };
        if (it.product_type === "accessory" && it.accessory_variant_id) {
          base.accessory_variant_id = String(it.accessory_variant_id);
        }
        return base;
      });

      const stockRes = await api.post(
        `${API_BASE_URL}/check_stock.php`,
        { items: validationItems },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );

      if (!stockRes.data?.success) {
        if (stockRes.data?.error === "price_mismatch") {
          setShowPriceModal(true);
          setShowAddressPopup(false);
          await refetchCartPrices();
          return false;
        }
        const code = stockRes.data?.error || "stock_unavailable";
        const detail = stockRes.data?.detail || stockRes.data?.at || "";
        const extra = detail ? `\n• ${detail}` : "";
        openStockModal(
          `${T("მარაგის პრობლემა", "Stock issue")}: ${code === 'stock_unavailable'
            ? T("მარაგი არ ჰყოფნის", "Not enough stock")
            : code}${extra}`
        );
        setStockModalOpen(true);
        return false;
      }

const response = await api.post(
  `${API_BASE_URL}/start_payment.php`,
  { ...purchaseData, lang: language },
  { headers: { "Content-Type": "application/json" }, withCredentials: true }
);

const data = response?.data || {};

// ✅ 1) success -> redirect
if (data?.success && data?.redirect_url) {
      setShowAddressPopup(false);
      setPayUrl(data.redirect_url);
      setPayModalOpen(true);
      return true;
}

// ✅ 2) price changed
if (data?.reason === "price_changed") {
  setShowPriceModal(true);
  setShowAddressPopup(false);
  await refetchCartPrices();
  return false;
}

// ✅ 3) insufficient funds / recurring_failed (ეს იყოს ზემოთ!)
if (String(data?.error || "") === "recurring_failed" || isInsufficientFunds(data)) {
  openStockModal(paymentFailText(data, language));
  return false;
}

// ✅ 4) stock issue
if (data?.error === "stock_unavailable") {
  openStockModal(
    T(
      "ბოდიში, ნივთი უკვე გაიყიდა ან მარაგი აღარ არის. სცადეთ რაოდენობის შემცირება ან განახლება.",
      "Sorry, the item was just sold or is out of stock. Try reducing the quantity or refreshing."
    )
  );
  return false;
}

// ✅ 5) fallback generic
openStockModal(paymentFailText(data, language));
return false;

    } catch (error) {
      console.error("❌ Network/API Error:", error);
      alert(
        language === "en"
          ? "❌ An error occurred while processing your purchase."
          : "❌ შესყიდვის პროცესში წარმოიშვა შეცდომა."
      );
    }
  };

  const handleDeleteItem = (index) => {
    if (typeof setCart !== "function") {
      console.error("❌ setCart is not a function!");
      return;
    }
    setCart(prevCart => {
      const updatedCart = prevCart.filter((_, i) => i !== index);
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      return updatedCart;
    });
    setSelectedOptions(prevOptions => {
      const updatedOptions = prevOptions.filter((_, i) => i !== index);
      localStorage.setItem("selectedOptions", JSON.stringify(updatedOptions));
      return updatedOptions;
    });
  };

  const updateQuantity = (index, value) => {
    setCart(prevCart => {
      const next = prevCart.map((item, i) =>
        i === index
          ? { ...item, quantity: Math.min(Math.max(1, value), item.full_stock) }
          : item
      );
      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      syncCartToServer(next);
      return next;
    });
  };

  const incrementQuantity = (index) => {
    setCart(prevCart => {
      const next = prevCart.map((item, i) =>
        i === index
          ? { ...item, quantity: Math.min((item.quantity||1) + 1, item.full_stock) }
          : item
      );
      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      syncCartToServer(next);
      return next;
    });
  };

  const decrementQuantity = (index) => {
    setCart(prevCart => {
      let next = [...prevCart];
      if ((next[index].quantity || 1) === 1) {
        next.splice(index, 1);
      } else {
        next[index] = { ...next[index], quantity: (next[index].quantity||1) - 1 };
      }
      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      syncCartToServer(next);
      return next;
    });
  };

const openStockModal = (msg) => {
  setShowAddressPopup(false);
  setShowPriceModal(false);
  setShowAuthModal(false);
  setStockMessage(String(msg || ""));
  setStockModalOpen(true);
};

  return (
    <CartWrapper isOpen={isOpen} ref={cartOverlayRef}>
      <CartContent>
        <CloseButton onClick={toggleCartOverlay}>×</CloseButton>
        <h2>{t("cartitems.choosenperfumes")} ({totalItems})</h2>

        <CartItems>
          {cart.map((item, index) => {
            const isPerfume = item.type === "perfume" || item.category === "perfume";
            const isGiftSet = item.type === "gift_set";

            // stock presence (per-ML units rows may have stock even if full_stock is 0)
            const inStock = (() => {
              if (isPerfume) {
                const mlList = Array.isArray(item.milliliters) ? item.milliliters.map(r=>withStrategy(r,item)) : [];
                const anyUnitsAvailable = mlList.some(ml => ml._strategy === "units" && Number(ml.unit_stock||0) > 0);
                const anyFullRows = mlList.some(ml => ml._strategy === "full");
                if (anyUnitsAvailable) return true;
                if (anyFullRows) return Number(item.full_stock || 0) > 0;
                // fallback
                return Number(item.full_stock || 0) > 0;
              }
              return Number(item.full_stock || 0) > 0;
            })();

            const mlThumbsCount = isPerfume
              ? (selectedOptions[index]?.selectedMilliliters || [])
                  .filter(x => Array.isArray(x.images) && x.images.length > 0).length
              : 0;
            const showMainImage = !(isPerfume && mlThumbsCount > 0);

            // If all "units" rows are 0 and there are no usable pooled ml, show out of stock
            const mlArr = Array.isArray(item.milliliters) ? item.milliliters.map(r=>withStrategy(r,item)) : [];
            const allMlUnitsZero = isPerfume && mlArr.length > 0 &&
              mlArr.every(ml => (ml._strategy === "units" ? Number(ml.unit_stock||0) <= 0 : true));
            const hasFullRows = mlArr.some(ml => ml._strategy === "full");
            const fullDepleted = hasFullRows && Number(item.full_stock || 0) <= 0;
            const showProductOut = isPerfume && (
              (allMlUnitsZero && !hasFullRows) || (allMlUnitsZero && hasFullRows && fullDepleted) || (!allMlUnitsZero && hasFullRows && fullDepleted && !mlArr.some(ml=>ml._strategy==="units" && Number(ml.unit_stock||0)>0))
            );

            return (
              <CartItem $noImage={!showMainImage} key={index}>
                {showMainImage && (
                  <ImageContainer>
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name || "item"}
                        onError={e => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </ImageContainer>
                )}

                <ItemDetails>
                  <h3 className="h3-item-details">
                    {isGiftSet ? (item.title || item.name || "Gift set") : (item.name_en ? item[t("detailed.name")] : item.name)}
                  </h3>

                  {showProductOut && (
                    <StockNote>{T("მარაგში აღარ არის", "Out of stock")}</StockNote>
                  )}

                  {isPerfume ? (
                    (selectedOptions[index]?.selectedMilliliters || [])
                      .map(rawMl => withStrategy(rawMl, item))
                      .sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price))
                      .map((ml, mlIndex) => {
                        // Disable conditions:
                        //  - units row: disable when not selected and unit_stock <= 0
                        //  - full row : disable when not selected and pooled remaining < size
                        let isDisabled;
                        if (ml._strategy === "units") {
                          isDisabled = !ml.isSelected && Number(ml.unit_stock || 0) <= 0;
                        } else {
                          const selected = (selectedOptions[index]?.selectedMilliliters || []).map(r=>withStrategy(r,item));
                          const totalFullSelected = selectedMlTotalFullRows(selected);
                          const remainingPool = (item.full_stock || 0) - (totalFullSelected - (ml.isSelected ? (Number(ml.quantity||0)*Number(ml.size||0)) : 0));
                          isDisabled = remainingPool < ml.size && !ml.isSelected;
                        }

                        const plusDisabled = (() => {
                          if (ml._strategy === "units") {
                            return (Number(ml.quantity||0) + 1) > Number(ml.unit_stock || 0);
                          }
                          // pooled
                          const selected = (selectedOptions[index]?.selectedMilliliters || []).map(r=>withStrategy(r,item));
                          const totalFullSelected = selectedMlTotalFullRows(selected);
                          const poolRemainingIfThisRemoved = (item.full_stock || 0) - (totalFullSelected - (ml.isSelected ? (Number(ml.quantity||0) * Number(ml.size||0)) : 0));
                          return poolRemainingIfThisRemoved < Number(ml.size || 1);
                        })();

                        return (
                          <MilliliterRow key={mlIndex} disabled={isDisabled}>
                            <TopRow>
                              <CheckboxWrapper>
                                {Array.isArray(ml.images) && ml.images.length > 0 && (
                                  <MlThumb
                                    src={ml.images[0]}
                                    alt={`${item.name} ${ml.size}ml`}
                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                  />
                                )}

                                <StyledCheckbox
                                  type="checkbox"
                                  checked={ml.isSelected}
                                  onChange={(e) =>
                                    handleMilliliterChange(index, ml.size, e.target.checked)
                                  }
                                  disabled={isDisabled}
                                />

                                <MilliliterLabel disabled={isDisabled}>
                                  {ml.size} {t("cartitems.ml")} {ml._strategy === "units" ? "• units" : "• full"}
                                </MilliliterLabel>
                              </CheckboxWrapper>

                              <span>
                                {ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price) ? (
                                  <>
                                    <span style={{ textDecoration: "line-through", color: "red", marginRight: 8 }}>
                                      {ml.price} {t("cartitems.currency")}
                                    </span>
                                    <strong>{ml.sale_price} {t("cartitems.currency")}</strong>
                                  </>
                                ) : (
                                  <strong>{ml.price} {t("cartitems.currency")}</strong>
                                )}
                              </span>
                            </TopRow>

                            {ml.isSelected && (
                              <QuantityWrapper>
                                <QuantityButton
                                  onClick={() => handleQuantityChange(index, ml.size, Number(ml.quantity||0) - 1)}
                                  disabled={Number(ml.quantity||0) <= 1}
                                >
                                  -
                                </QuantityButton>

                                <QuantityInput
                                  type="number"
                                  min="1"
                                  value={ml.quantity || 1}
                                  onChange={(e) =>
                                    handleQuantityChange(index, ml.size, parseInt(e.target.value))
                                  }
                                />

                                <QuantityButton
                                  onClick={() => handleQuantityChange(index, ml.size, Number(ml.quantity||0) + 1)}
                                  disabled={plusDisabled}
                                >
                                  +
                                </QuantityButton>
                              </QuantityWrapper>
                            )}

                            {isDisabled && (
                              <StockNote>
                                {T("მარაგში აღარ არის", "Out of stock")}
                              </StockNote>
                            )}
                          </MilliliterRow>
                        );
                      })
                  ) : isGiftSet ? (
                    <>
                      <Price $muted={!inStock}>
                        <strong>
                          {(Number(item.unit_price || 0) * (item.quantity || 0)).toFixed(2)} {t("cartitems.currency")}
                        </strong>
                      </Price>
                      {!inStock && <StockNote>{T("მარაგში აღარ არის", "Out of stock")}</StockNote>}
                      {inStock && (
                        <QuantityWrapper>
                          <QuantityButton
                            onClick={() => decrementQuantity(index)}
                            disabled={(item.quantity || 1) <= 1}
                          >
                            -
                          </QuantityButton>
                          <QuantityInput
                            type="number"
                            min="1"
                            value={item.quantity || 1}
                            onChange={(e) => updateQuantity(index, parseInt(e.target.value))}
                          />
                          <QuantityButton
                            onClick={() => incrementQuantity(index)}
                            disabled={(item.quantity || 1) >= item.full_stock}
                          >
                            +
                          </QuantityButton>
                        </QuantityWrapper>
                      )}
                    </>
                  ) : (
                    <>
                      {/* ACCESSORIES */}
                      <Price $muted={!inStock}>
                        {item.sale_p && parseFloat(item.sale_p) < parseFloat(item.price_p) ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "red", marginRight: "8px" }}>
                              {(parseFloat(item.price_p)).toFixed(2)} {t("cartitems.currency")}
                            </span>
                            <strong style={{ color: "green" }}>
                              {(parseFloat(item.sale_p)).toFixed(2)} {t("cartitems.currency")}
                            </strong>
                          </>
                        ) : (
                          <strong>
                            {(parseFloat(item.price_p)).toFixed(2)} {t("cartitems.currency")}
                          </strong>
                        )}
                      </Price>

                      {/* color + size */}
                      {(() => {
                        const colorKa = item?.selectedVariant?.color_ka ?? item?.color_ka ?? item?.color ?? "";
                        const colorEn = item?.selectedVariant?.color_en ?? item?.color_en ?? "";
                        const sizeLbl = item?.selectedVariant?.size_label ?? item?.size_label ?? "";

                        const displayColor = (language === "ka" ? (colorKa || colorEn) : (colorEn || colorKa)) || "";

                        if (!displayColor && !sizeLbl) return null;

                        return (
                          <div style={{ marginTop: 4, color: "#6b7280", fontWeight: 800 }}>
                            {displayColor}{sizeLbl ? ` · ${sizeLbl}` : ""}
                          </div>
                        );
                      })()}
                      {!inStock && <StockNote>{T("მარაგში აღარ არის", "Out of stock")}</StockNote>}
                      {inStock && (
                        <QuantityWrapper>
                          <QuantityButton onClick={() => decrementQuantity(index)} disabled={(item.quantity||0) <= 1}>-</QuantityButton>
                          <QuantityInput type="number" min="1" value={item.quantity||1} onChange={(e) => updateQuantity(index, parseInt(e.target.value))} />
                          <QuantityButton onClick={() => incrementQuantity(index)} disabled={(item.quantity||0) >= item.full_stock}>+</QuantityButton>
                        </QuantityWrapper>
                      )}
                    </>
                  )}

                  <DeleteButton onClick={() => deleteItemFromCart(index)}>
                    {t("cartitems.delete")}
                  </DeleteButton>
                </ItemDetails>
              </CartItem>
            );
          })}
        </CartItems>

        <PriceChangedModal
          show={showPriceModal}
          onClose={async () => { 
            setShowPriceModal(false); 
            await refetchCartPrices(); 
          }}
          language={language}
        />

        <AuthExpiredModal
          visible={showAuthModal}
          language={language}
          onClose={() => {
            setShowAuthModal(false);
            localStorage.removeItem("user");
            window.location.href = "/login";
          }}
        />

        <div style={{ marginTop: 14, fontWeight: 900 }}>
          <div>
            {language === "ka" ? "შეკვეთა" : "Order"}:{" "}
            {Number(totalPrice).toFixed(2)} {t("cartitems.currency")}
          </div>

          <div>
            {language === "ka" ? "მიწოდება" : "Delivery"}:{" "}
            {Number(deliveryFee).toFixed(2)} {t("cartitems.currency")}
          </div>

          <hr />

          <div>
            {t("cartitems.total")}:{" "}
            {(Number(totalPrice) + Number(deliveryFee)).toFixed(2)} {t("cartitems.currency")}
          </div>
        </div>
        <BuyNow
          onClick={async () => {
            const storedUser = localStorage.getItem("user");
            const currentUser = storedUser ? JSON.parse(storedUser) : null;
            if (!currentUser?.id) {
              setShowRegisterModal(true);
            } else {
              await openAddressPopup();
            }
          }}
          disabled={totalItems === 0}
        >
          {t("cartitems.buy")}
        </BuyNow>
      </CartContent>

      {showRegisterModal && (
        <MiniRegisterModal
          onClose={() => setShowRegisterModal(false)}
          onSubmitted={async (payload) => {
            try {
              const res = await api.post(
                `${API_BASE_URL}/guest_start.php`,
                { ...payload, lang: language },
                { headers: { "Content-Type": "application/json" }, withCredentials: true }
              );
              const data = res.data;
              if (data?.success) {
                setTempUser({
                  id: data.guest_user_id || data.user_id || data?.user?.id,
                  type: "guest",
                  email: payload?.email,
                });
                setShowRegisterModal(false);
                const guessedHasPhone = !!payload?.phone || !!payload?.phone_e164;
                setPopupAssumeHasPhone(guessedHasPhone);
                setShowAddressPopup(true);
              }
              return data;
            } catch (e) {
              console.error(e);
              return { success: false, error_code: "NETWORK", error: "Network error" };
            }
          }}
        />
      )}

      {stockModalOpen && (
        <StockModal
          message={stockMessage}
          onClose={() => setStockModalOpen(false)}
        />
      )}

      {payModalOpen && payUrl && (
  <PaymentIframeModal
    url={payUrl}
    language={language}
    onClose={() => {
      setPayModalOpen(false);
      setPayUrl("");

      // fallback same-window open
      // window.location.assign(payUrl);  // ან popup
    }}
  />
)}


      {showAddressPopup && (
        <AddressPopup
          onClose={() => setShowAddressPopup(false)}
          onSubmit={async (data) => {
            const ok = await handleBuyNow(data);
            if (ok) setShowAddressPopup(false);
          }}
          assumeHasPhone={popupAssumeHasPhone}
        />
      )}
    </CartWrapper>
  );
};

const BuyNow = styled.button`
  background-color:rgb(20, 20, 20);
  color: white;
  border: none;
  padding: 5px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 900;
  &:hover { background-color:rgb(48, 48, 48); }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const CartWrapper = styled.div`
  position: fixed;
  top: 0;
  right: ${({ isOpen }) => (isOpen ? "0" : "-400px")};
  height: 100%;
  width: 400px;
  background: white;
  transition: right 0.3s ease-in-out;
  overflow-y: auto;
  z-index: 3001;
  font-size: 0.8rem;
  box-shadow: -5px 0 10px rgba(0, 0, 0, 0.1);
`;

const CartContent = styled.div`
  padding: 20px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const CartItems = styled.div`
  overflow-y: auto;
  max-height: 60vh;
  padding-right: 10px;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #a16a6a, #702e2e); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #702e2e, #4d1e1e); }
`;

const CartItem = styled.div`
  display: flex;
  gap: 15px;
  padding: 10px;
  align-items: flex-start;
  ${({ $noImage }) => $noImage && `padding-left: 10px;`}
`;

const ImageContainer = styled.div`
  width: 50px;
  flex: 0 0 50px;
  img { width: 100%; border-radius: 8px; display: block; }
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const DeleteButton = styled.button`
  background-color: #ff4d4f;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 5px;
  font-weight: 900;
  &:hover { background-color: #ff7875; }
`;

const MilliliterRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: #f7f7f7;
  border-radius: 8px;
  margin: 6px 0;
  width: 90%;
  border: 2px solid transparent;
  transition: 0.3s ease-in-out;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  &:hover { border-color:rgb(45, 45, 45); background: #eef4ff; }
`;

const TopRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding-bottom: 5px;
`;

const CheckboxWrapper = styled.label`
  display: flex; align-items: center; gap: 8px; cursor: pointer;
`;

const QuantityButton = styled.button`
  background: rgb(45, 45, 45);
  color: white; border: none;
  width: 30px; height: 30px; border-radius: 50%;
  font-size: 18px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.3s ease;
  &:hover { background: rgb(73, 73, 73); }
  &:disabled { background-color: #f0f0f0; color: #999; cursor: not-allowed; }
`;

const QuantityInput = styled.input`
  width: 50px; text-align: center; padding: 5px;
  border: 1px solid #ccc; border-radius: 4px; font-size: 16px; background: white;
  appearance: none; -moz-appearance: textfield;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
`;

const StyledCheckbox = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  width: 20px; height: 20px; border: 2px solid #333; border-radius: 5px;
  display: flex; align-items: center; justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  background-color: ${({ checked }) => (checked ? "#333" : "#fff")};
  position: relative; transition: all 0.2s ease-in-out;
  &:hover { border-color: ${({ disabled }) => (disabled ? "#aaa" : "#555")}; }
  &:checked::before {
    content: "✔"; font-size: 14px; color: #fff; font-weight: bold;
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  }
  &:disabled { border-color: #aaa; background-color: #eee; cursor: not-allowed; }
`;

const MilliliterLabel = styled.span`
  font-size: 1rem;
  color: ${({ disabled }) => (disabled ? "#aaa" : "#000")};
  text-decoration: ${({ disabled }) => (disabled ? "line-through" : "none")};
`;

const QuantityWrapper = styled.div`
  display: flex; align-items: center; gap: 5px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

const Collage = styled.div`
  position: relative;
  width: 50px; height: 50px;
`;
const CollageImg = styled.img`
  position: absolute;
  width: 34px; height: 34px;
  object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb;
`;

export default CartOverlay;
